import {
  Add,
  AttachFile,
  AutoAwesome,
  BusinessCenter,
  Close,
  Delete,
  ExpandMore,
  Info,
  Link,
  LinkedIn,
  Settings,
  Business,
  Person,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Switch,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import KnowledgeBaseSelect from "../../components/KnowledgeBaseSelect";
import LoadingButton from "../../components/LoadingButton";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";

interface BrandVoiceGenerateProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  company: Company;
  getToken: Function;
  eventListener: Function;
  saveVoice: Function;
}

export default ({
  open,
  onClose,
  companyId,
  company,
  getToken,
  eventListener,
  saveVoice,
}: BrandVoiceGenerateProps) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState<any>({
    name: "",
    industry: "",
    audience: "",
    description: "",
    useCompanyInfo: true,
    useKnowledgeBase: false,
    knowledgeBasePath: "/",
    linkedinUrl: "",
    useLinkedInProfile: false,
    linkedInProfileType: "person",
    usePremiumProxy: false,
    brandLinks: [] as string[],
  });
  const [newBrandLink, setNewBrandLink] = useState("");
  const [expandedPanel, setExpandedPanel] = useState<string | false>(
    "companyPanel"
  );

  const handleExpandPanel =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Initialize form with company data if useCompanyInfo is true
      const initialData = {
        name: `${
          company?.name || "Company"
        } Brand Voice - ${new Date().toLocaleDateString()}`,
        industry: "",
        audience: "",
        description: "",
        useCompanyInfo: true,
        useKnowledgeBase: false,
        knowledgeBasePath: "/",
        linkedinUrl: "",
        useLinkedInProfile: false,
        linkedInProfileType: "person",
        usePremiumProxy: false,
        brandLinks: [] as string[],
      };
      setNewBrandLink("");

      // If useCompanyInfo is true and company data exists, populate fields
      if (initialData.useCompanyInfo && company) {
        initialData.industry = company.industry || "";
        initialData.description = company.description || "";
      }

      setFormData(initialData);
    }
  }, [open, company]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData({ ...formData, [name]: checked });

    // If toggling useCompanyInfo, populate or clear the company fields
    if (name === "useCompanyInfo") {
      if (checked && company) {
        // Populate fields with current company data
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          industry: company.industry || "",
          description: company.description || "",
        });
      } else if (!checked) {
        // Clear company-related fields
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          industry: "",
          description: "",
        });
      }
    }
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddBrandLink = () => {
    const trimmedLink = newBrandLink.trim();
    if (
      trimmedLink &&
      !formData.brandLinks.includes(trimmedLink) &&
      formData.brandLinks.length < 5
    ) {
      setFormData({
        ...formData,
        brandLinks: [...formData.brandLinks, trimmedLink],
      });
      setNewBrandLink("");
    }
  };

  const handleRemoveBrandLink = (index: number) => {
    const newLinks = formData.brandLinks.filter(
      (_: string, i: number) => i !== index
    );
    setFormData({ ...formData, brandLinks: newLinks });
  };

  const generateVoice = async () => {
    if (!formData.name) {
      eventListener({
        type: "SET_ERROR",
        error: "Brand voice name is required",
      });
      return;
    }

    // Check if LinkedIn profile generation is selected
    if (formData.useLinkedInProfile && !formData.linkedinUrl.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "LinkedIn URL is required when using LinkedIn profile",
      });
      return;
    }

    setGenerating(true);

    try {
      const token = await getToken();

      if (formData.useLinkedInProfile) {
        // Use LinkedIn generation endpoint
        const response = await axios.post(
          `${hostname}/content/brand-voices/generate-from-linkedin`,
          {
            companyId,
            linkedin_url: formData.linkedinUrl,
            profile_type: formData.linkedInProfileType,
            premium: formData.usePremiumProxy,
            brand_links: formData.brandLinks,
            // Don't send voice_name - let the backend use the LinkedIn profile name
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          await saveVoice(response.data.data);
          eventListener({
            type: "SET_SUCCESS",
            message: `Successfully created brand voice from ${response.data.profile_name}'s LinkedIn profile!`,
          });
        } else {
          throw new Error(response.data.error || "LinkedIn generation failed");
        }
      } else {
        // Use regular generation endpoint
        const inputData = {
          name: formData.name,
          industry: formData.industry,
          audience: formData.audience,
          description: formData.description,
          useCompanyInfo: formData.useCompanyInfo,
          useKnowledgeBase: formData.useKnowledgeBase,
          knowledgeBasePath: formData.knowledgeBasePath,
          brandLinks: formData.brandLinks,
        };

        await axios.post(
          `${hostname}/content/brand-voices/generate`,
          {
            companyId,
            data: inputData,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Show success message indicating background generation started
        eventListener({
          type: "SET_SUCCESS",
          info: "Brand voice generation started! This may take a few minutes. Check back shortly to see your completed brand voice.",
        });

        // Call saveVoice with null to signal completion and trigger a refresh
        // The voice is already created in the backend as a placeholder
        await saveVoice(null);
      }
    } catch (error) {
      console.error("Error generating brand voice:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setGenerating(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AutoAwesome color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Generate Brand Voice with AI</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>How This Works</AlertTitle>
          Our AI will analyze your company information and knowledge base
          documents to create a comprehensive Brand Voice Profile that aligns
          with your brand identity.
        </Alert>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TextField
          name="name"
          label="Brand Voice Name"
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          required
          margin="normal"
          error={!formData.name}
          helperText={!formData.name ? "Brand voice name is required" : ""}
        />

        <Accordion
          expanded={expandedPanel === "companyPanel"}
          onChange={handleExpandPanel("companyPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <BusinessCenter sx={{ mr: 1 }} />
            <Typography>Brand Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useCompanyInfo}
                  onChange={handleSwitchChange}
                  name="useCompanyInfo"
                />
              }
              label="Use current company information as a starting point"
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="industry"
                    value={formData.industry}
                    label="Industry"
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="audience"
                    value={formData.audience}
                    label="Target Audience"
                    onChange={handleInputChange}
                    placeholder="e.g., B2B Tech Executives, Small business owners"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Brand Description"
                    value={formData.description}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Describe your brand and what makes it unique"
                  />
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "knowledgePanel"}
          onChange={handleExpandPanel("knowledgePanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <AttachFile sx={{ mr: 1 }} />
            <Typography>Knowledge Base Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useKnowledgeBase}
                  onChange={handleSwitchChange}
                  name="useKnowledgeBase"
                />
              }
              label="Include knowledge base documents"
            />

            {formData.useKnowledgeBase && (
              <Box sx={{ mt: 2 }}>
                <KnowledgeBaseSelect
                  getToken={getToken}
                  eventListener={eventListener}
                  company={company}
                  value={formData.knowledgeBasePath}
                  onValueChange={(v) =>
                    setFormData({ ...formData, knowledgeBasePath: v })
                  }
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "brandLinksPanel"}
          onChange={handleExpandPanel("brandLinksPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Link sx={{ mr: 1 }} />
            <Typography>Brand Links</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              Add links to brand websites, blog posts, articles, or other
              writing samples to help the AI better understand the brand voice
              and writing style. (Max 5 links)
            </Typography>

            {formData.brandLinks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {formData.brandLinks.map((link: string, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      p: 1,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Link
                      sx={{ mr: 1, fontSize: 16, color: "primary.main" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {link}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveBrandLink(index)}
                      sx={{ ml: 1 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                placeholder="https://example.com/blog-post"
                value={newBrandLink}
                onChange={(e) => setNewBrandLink(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddBrandLink();
                  }
                }}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleAddBrandLink}
                disabled={
                  !newBrandLink.trim() || formData.brandLinks.length >= 5
                }
                startIcon={<Add />}
              >
                Add
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "linkedinPanel"}
          onChange={handleExpandPanel("linkedinPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <LinkedIn sx={{ mr: 1 }} />
            <Typography>LinkedIn Profile</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useLinkedInProfile}
                  onChange={handleSwitchChange}
                  name="useLinkedInProfile"
                />
              }
              label="Generate brand voice from a LinkedIn profile"
            />

            {formData.useLinkedInProfile && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>LinkedIn Profile Generation</AlertTitle>
                  Generate a brand voice from a LinkedIn person or company profile based on their professional background and communication style.
                </Alert>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Profile Type
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.linkedInProfileType}
                    exclusive
                    onChange={(event, newType) => {
                      if (newType !== null) {
                        setFormData({ ...formData, linkedInProfileType: newType });
                      }
                    }}
                    aria-label="LinkedIn profile type"
                    fullWidth
                  >
                    <ToggleButton value="person" aria-label="person profile">
                      <Person sx={{ mr: 1 }} />
                      Person Profile
                    </ToggleButton>
                    <ToggleButton value="company" aria-label="company profile">
                      <Business sx={{ mr: 1 }} />
                      Company Page
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  name="linkedinUrl"
                  label="LinkedIn URL"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder={
                    formData.linkedInProfileType === "person"
                      ? "https://linkedin.com/in/username"
                      : "https://linkedin.com/company/company-name"
                  }
                  helperText={
                    formData.linkedInProfileType === "person"
                      ? "Enter the full LinkedIn profile URL (e.g., https://linkedin.com/in/johndoe)"
                      : "Enter the full LinkedIn company page URL (e.g., https://linkedin.com/company/microsoft)"
                  }
                  error={formData.useLinkedInProfile && !formData.linkedinUrl.trim()}
                  sx={{ mb: 2 }}
                />
                
                <FormControlLabel
                  control={
                    <Tooltip title="Residential proxy provides better success rates for accessing LinkedIn profiles but uses more credits (10 vs 5)">
                      <Switch
                        checked={formData.usePremiumProxy}
                        onChange={handleSwitchChange}
                        name="usePremiumProxy"
                      />
                    </Tooltip>
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Use Premium Proxy (Residential IP)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Better success rate • 10 credits (vs 5 for standard)
                      </Typography>
                    </Box>
                  }
                />
                
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  {formData.linkedInProfileType === "person"
                    ? "The AI will analyze the person's experience, skills, activities, and communication patterns to create an authentic professional brand voice."
                    : "The AI will analyze the company's about section, specialties, industry focus, and recent updates to create a brand voice aligned with their corporate identity."}
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "advancedPanel"}
          onChange={handleExpandPanel("advancedPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Settings sx={{ mr: 1 }} />
            <Typography>Advanced Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                The AI will analyze your inputs to generate detailed information
                about:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>Brand personality traits and tone of voice</li>
                <li>Communication style and formality level</li>
                <li>Language guidelines including vocabulary and phrases</li>
                <li>Content structure and organization preferences</li>
                <li>Visual elements and distinctive brand features</li>
              </Box>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={generateVoice}
          loading={generating}
          disabled={generating || !formData.name}
          startIcon={<AutoAwesome />}
        >
          {generating ? "Generating..." : "Generate Brand Voice"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
